/**
 * セキュアなトークンストレージ実装
 *
 * アクセストークンやリフレッシュトークンを安全に保存・取得するためのユーティリティ
 */

import { StorageError } from '@src/errors';
import { OptimizedLocalStorage } from '@src/modules/optimization';
import { errorLogger } from '@src/services/errorLogger';
import { parseISO } from 'date-fns';

/**
 * 簡易暗号化・復号化
 * 注意: これは基本的な難読化で、真の暗号化ではありません
 * 本番環境ではより強力な暗号化ライブラリの使用を推奨
 */

/**
 * トークンを暗号化（難読化）
 */
function encryptToken(token: string): string {
  try {
    // Base64エンコードによる基本的な難読化
    const encoded = btoa(token);
    // 簡易的な置換による追加の難読化
    return encoded.split('').reverse().join('');
  } catch {
    return token; // 暗号化に失敗した場合は平文を返す
  }
}

/**
 * トークンを復号化
 */
function decryptToken(encryptedToken: string): string {
  try {
    // 置換を元に戻す
    const reversed = encryptedToken.split('').reverse().join('');
    // Base64デコード
    return atob(reversed);
  } catch {
    return encryptedToken; // 復号化に失敗した場合は元の値を返す
  }
}

/**
 * セキュアなトークンストレージ
 */
const ACCESS_TOKEN_KEY = 'auth_access_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const EXPIRES_AT_KEY = 'auth_expires_at';

const storage = OptimizedLocalStorage.getInstance();

/**
 * アクセストークンを保存
 */
export function setAccessToken(token: string): void {
  try {
    const encrypted = encryptToken(token);
    storage.set(ACCESS_TOKEN_KEY, encrypted, 0);
  } catch (error) {
    const storageError = new StorageError('アクセストークンの保存に失敗', undefined, {
      cause: error,
    });
    errorLogger.logError(storageError, {
      context: 'トークン保存',
      action: 'setAccessToken',
    });
  }
}

/**
 * アクセストークンを取得
 */
export function getAccessToken(): string | null {
  try {
    const encrypted = storage.get(ACCESS_TOKEN_KEY, null);
    return encrypted ? decryptToken(encrypted) : null;
  } catch (error) {
    const storageError = new StorageError('アクセストークンの取得に失敗', undefined, {
      cause: error,
    });
    errorLogger.logError(storageError, {
      context: 'トークン取得',
      action: 'getAccessToken',
    });
    return null;
  }
}

/**
 * リフレッシュトークンを保存
 */
export function setRefreshToken(token: string): void {
  try {
    const encrypted = encryptToken(token);
    storage.set(REFRESH_TOKEN_KEY, encrypted, 0);
  } catch (error) {
    const storageError = new StorageError('リフレッシュトークンの保存に失敗', undefined, {
      cause: error,
    });
    errorLogger.logError(storageError, {
      context: 'トークン保存',
      action: 'setRefreshToken',
    });
  }
}

/**
 * リフレッシュトークンを取得
 */
export function getRefreshToken(): string | null {
  try {
    const encrypted = storage.get(REFRESH_TOKEN_KEY, null);
    return encrypted ? decryptToken(encrypted) : null;
  } catch (error) {
    const storageError = new StorageError('リフレッシュトークンの取得に失敗', undefined, {
      cause: error,
    });
    errorLogger.logError(storageError, {
      context: 'トークン取得',
      action: 'getRefreshToken',
    });
    return null;
  }
}

/**
 * トークンの有効期限を保存
 */
export function setExpiresAt(expiresAt: string): void {
  try {
    storage.set(EXPIRES_AT_KEY, expiresAt, 0);
  } catch (error) {
    const storageError = new StorageError('トークン有効期限の保存に失敗', undefined, {
      cause: error,
    });
    errorLogger.logError(storageError, {
      context: 'トークン保存',
      action: 'setExpiresAt',
    });
  }
}

/**
 * トークンの有効期限を取得
 */
export function getExpiresAt(): string | null {
  try {
    return storage.get(EXPIRES_AT_KEY, null);
  } catch (error) {
    const storageError = new StorageError('トークン有効期限の取得に失敗', undefined, {
      cause: error,
    });
    errorLogger.logError(storageError, {
      context: 'トークン取得',
      action: 'getExpiresAt',
    });
    return null;
  }
}

/**
 * 全てのトークンを削除
 */
export function clearAllTokens(): void {
  try {
    storage.remove(ACCESS_TOKEN_KEY);
    storage.remove(REFRESH_TOKEN_KEY);
    storage.remove(EXPIRES_AT_KEY);
  } catch (error) {
    const storageError = new StorageError('トークンのクリアに失敗', undefined, { cause: error });
    errorLogger.logError(storageError, {
      context: 'トークン削除',
      action: 'clearAllTokens',
    });
  }
}

/**
 * トークンが有効期限内かチェック
 */
export function isTokenValid(): boolean {
  try {
    const expiresAt = getExpiresAt();
    if (!expiresAt) {
      return false;
    }

    const expirationTime = parseISO(expiresAt).getTime();
    const currentTime = Date.now();

    return currentTime < expirationTime;
  } catch {
    return false;
  }
}
